/**
 * Environment Configuration
 * Validates and exports environment variables with type safety
 * Now with enhanced validation and better error messages
 */

interface EnvConfig {
  // API Configuration
  API_ENDPOINT: string;
  WEBSITE_ENDPOINT?: string;
  IMAGE_ENDPOINT?: string;

  // Google Maps
  GOOGLE_MAPS_KEY?: string;

  // Razorpay
  RAZORPAY_KEY_ID?: string;
  RAZORPAY_KEY_SECRET?: string;

  // Firebase
  FIREBASE_API_KEY?: string;
  FIREBASE_AUTH_DOMAIN?: string;
  FIREBASE_PROJECT_ID?: string;
  FIREBASE_STORAGE_BUCKET?: string;
  FIREBASE_MESSAGING_SENDER_ID?: string;
  FIREBASE_APP_ID?: string;

  // Environment
  NODE_ENV?: string;
  DEV_MODE?: string;
  DEV: boolean;
}

/**
 * Environment variable configuration with descriptions
 */
interface EnvVarConfig {
  key: string;
  required: boolean;
  description: string;
  validator?: (value: string) => boolean;
  errorMessage?: string;
}

const ENV_VAR_CONFIGS: EnvVarConfig[] = [
  {
    key: 'VITE_API_ENDPOINT',
    required: true,
    description: 'Backend API endpoint URL',
    validator: (value) => value.startsWith('http://') || value.startsWith('https://'),
    errorMessage: 'API_ENDPOINT must be a valid HTTP/HTTPS URL',
  },
  {
    key: 'VITE_GOOGLE_MAPS_KEY',
    required: false,
    description: 'Google Maps API key for location features',
  },
  {
    key: 'VITE_FIREBASE_API_KEY',
    required: false,
    description: 'Firebase API key for authentication',
  },
  {
    key: 'VITE_RAZORPAY_KEY_ID',
    required: false,
    description: 'Razorpay key ID for payment processing',
  },
];

/**
 * Validate environment variables with detailed error messages
 * @throws Error with helpful information if validation fails
 */
export const validateEnv = (): void => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Debug: Log what Vite is seeing
  console.log('DEBUG - import.meta.env:', import.meta.env);
  console.log('DEBUG - VITE_API_ENDPOINT value:', import.meta.env.VITE_API_ENDPOINT);

  for (const config of ENV_VAR_CONFIGS) {
    const value = import.meta.env[config.key];
    
    // Check if required variable is missing
    if (config.required && (!value || value === '')) {
      errors.push(`  ❌ ${config.key}`);
      errors.push(`     ${config.description}`);
      errors.push('');
    }
    
    // Check if optional variable is missing
    if (!config.required && (!value || value === '')) {
      warnings.push(`  ⚠️  ${config.key}`);
      warnings.push(`     ${config.description}`);
      warnings.push(`     (Optional - feature may not work without it)`);
      warnings.push('');
    }
    
    // Run custom validator if present
    if (value && config.validator && !config.validator(value)) {
      errors.push(`  ❌ ${config.key}`);
      errors.push(`     ${config.errorMessage || 'Invalid value'}`);
      errors.push('');
    }
  }

  // Display warnings in development
  if (warnings.length > 0 && import.meta.env.DEV) {
    console.warn('⚠️  Missing optional environment variables:\n' + warnings.join('\n'));
  }

  // Throw error if required variables are missing
  if (errors.length > 0) {
    throw new Error(
      `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
      `❌ Environment Configuration Error\n` +
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
      `Missing or invalid required environment variables:\n\n` +
      errors.join('\n') +
      `\n📝 To fix this:\n` +
      `   1. Copy .env.example to .env in your project root\n` +
      `   2. Fill in the required values\n` +
      `   3. Restart the development server\n\n` +
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`
    );
  }
};

/**
 * Validated and typed environment configuration
 */
export const env: EnvConfig = {
  // API Configuration (Required)
  API_ENDPOINT: import.meta.env.VITE_API_ENDPOINT || '',
  
  // Optional API endpoints with fallbacks
  WEBSITE_ENDPOINT: import.meta.env['VITE_WEBSITE_ENDPOINT'],
  IMAGE_ENDPOINT: import.meta.env['VITE_IMAGE_ENDPOINT'],

  // Google Maps
  GOOGLE_MAPS_KEY: import.meta.env['VITE_GOOGLE_MAPS_KEY'],

  // Razorpay
  RAZORPAY_KEY_ID: import.meta.env['VITE_RAZORPAY_KEY_ID'],
  RAZORPAY_KEY_SECRET: import.meta.env['VITE_RAZORPAY_KEY_SECRET'],

  // Firebase
  FIREBASE_API_KEY: import.meta.env['VITE_FIREBASE_API_KEY'],
  FIREBASE_AUTH_DOMAIN: import.meta.env['VITE_FIREBASE_AUTH_DOMAIN'],
  FIREBASE_PROJECT_ID: import.meta.env['VITE_FIREBASE_PROJECT_ID'],
  FIREBASE_STORAGE_BUCKET: import.meta.env['VITE_FIREBASE_STORAGE_BUCKET'],
  FIREBASE_MESSAGING_SENDER_ID: import.meta.env['VITE_FIREBASE_MESSAGING_SENDER_ID'],
  FIREBASE_APP_ID: import.meta.env['VITE_FIREBASE_APP_ID'],

  // Environment
  NODE_ENV: import.meta.env.MODE,
  DEV_MODE: import.meta.env.DEV ? 'true' : 'false',
  DEV: import.meta.env.DEV,
};

/**
 * Check if running in development mode
 */
export const isDevelopment = (): boolean => {
  return env.DEV || env.NODE_ENV === 'development';
};

/**
 * Check if running in production mode
 */
export const isProduction = (): boolean => {
  return !env.DEV && env.NODE_ENV === 'production';
};

/**
 * Get environment variable status for debugging
 * Only available in development mode
 */
export const getEnvStatus = (): Record<string, unknown> => {
  if (!isDevelopment()) {
    return { error: 'Environment status only available in development mode' };
  }

  return {
    configured: {
      API_ENDPOINT: !!env.API_ENDPOINT,
      GOOGLE_MAPS: !!env.GOOGLE_MAPS_KEY,
      FIREBASE: !!env.FIREBASE_API_KEY,
      RAZORPAY: !!env.RAZORPAY_KEY_ID,
    },
    values: {
      API_ENDPOINT: env.API_ENDPOINT,
      NODE_ENV: env.NODE_ENV,
      DEV_MODE: env.DEV,
    },
  };
};

/**
 * Initialize and validate environment on app startup
 * Provides detailed feedback about configuration status
 */
export const initializeEnv = (): void => {
  try {
    validateEnv();
    
    if (isDevelopment()) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('✅ Environment Configuration Validated');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📍 API Endpoint:', env.API_ENDPOINT);
      console.log('🌍 Environment:', env.DEV ? 'Development' : 'Production');
      
      // Log configured optional features
      const features: string[] = [];
      if (env.GOOGLE_MAPS_KEY) features.push('Google Maps');
      if (env.FIREBASE_API_KEY) features.push('Firebase Auth');
      if (env.RAZORPAY_KEY_ID) features.push('Razorpay Payments');
      
      if (features.length > 0) {
        console.log('🔧 Configured Features:', features.join(', '));
      }
      
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }
  } catch (error) {
    if (error instanceof Error) {
      console.error(error.message);
    } else {
      console.error('❌ Environment validation failed:', error);
    }
    throw error;
  }
};
